import * as React from "react";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Alert, Button, Collapse, Stack, Step, StepContent, StepLabel, Stepper, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";

interface Props {
  t: (key: string) => string
  hasStoreStepLabel: string
  noStoreTitle: string
  noStoreDescription: React.ReactNode
  connectStepLabel: string
  connectSteps: React.ReactNode[]
  connectButtonLabel: string
  connectUrl: string
  startConnectRequested?: boolean
  startConnectUrl?: string
  connectInfo?: React.ReactNode
  connectErrorMessage?: string | null
  isConnected: boolean
  isFlagged?: boolean
  isMissingScope?: boolean
  flagErrorMessage?: string | null
  connectedContent: React.ReactNode
}

export default function StoreConnectionStepper(props: Props) {
  const [activeStep, setActiveStep] = useState(0);
  const [hasStore, setHasStore] = useState<boolean | null>(null);
  const [errorDetailsOpened, setErrorDetailsOpened] = useState(false);
  const connectStartedRef = useRef(false);

  useEffect(() => {
    if ([props.isConnected, props.isFlagged, props.isMissingScope].some(Boolean)) {
      setActiveStep(2);
      return;
    }

    if (props.connectErrorMessage) {
      setActiveStep(1);
      return;
    }

    setActiveStep(value => value >= 2 ? 0 : value);
  }, [props.connectErrorMessage, props.isConnected, props.isFlagged, props.isMissingScope]);

  useEffect(() => {
    if (!props.startConnectRequested || !props.startConnectUrl || connectStartedRef.current) return;

    connectStartedRef.current = true;
    window.location.assign(props.startConnectUrl);
  }, [props.startConnectRequested, props.startConnectUrl]);

  return <Stepper activeStep={activeStep} orientation="vertical">
    <Step>
      <StepLabel>{props.hasStoreStepLabel}</StepLabel>
      <StepContent>
        <Stack spacing={2}>
          <Collapse in={hasStore === null}>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={() => {
                  setHasStore(true);
                  setActiveStep(1);
                }}
              >{props.t("Yes")}</Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setHasStore(false);
                }}
              >{props.t("No")}</Button>
            </Stack>
          </Collapse>

          <Collapse in={hasStore === false}>
            <Stack alignItems="flex-start" spacing={1}>
              <Typography variant="h4">{props.noStoreTitle}</Typography>
              {props.noStoreDescription}
              <Button
                variant="contained"
                onClick={() => {
                  setHasStore(true);
                  setActiveStep(1);
                }}
              >{props.t("Continue")}</Button>
            </Stack>
          </Collapse>
        </Stack>
      </StepContent>
    </Step>

    <Step>
      <StepLabel
        error={!!props.connectErrorMessage}
        optional={props.connectErrorMessage
          ? <Typography variant="caption" color="error">{props.t("Error connecting to your store")}</Typography>
          : null
        }
      >{props.connectStepLabel}</StepLabel>
      <StepContent>
        <Stack spacing={2} alignItems="flex-start">
          {props.connectErrorMessage ? <Alert severity="error">{props.connectErrorMessage}</Alert> : null}
          {props.connectInfo}
          <ol style={{ margin: 0, paddingLeft: "1.25rem" }}>
            {props.connectSteps.map((step, index) => <li key={index}>{step}</li>)}
          </ol>
          <Button
            variant="contained"
            component={Link}
            to={props.connectUrl}
          >{props.connectButtonLabel}</Button>
        </Stack>
      </StepContent>
    </Step>

    <Step>
      <StepLabel
        error={!!props.isFlagged}
        optional={props.isFlagged
          ? <Typography variant="caption" color="error">{props.t("Error connecting to your store")}</Typography>
          : null
        }
      >{props.isFlagged
        ? props.t("Setup error")
        : (props.isMissingScope ? props.t("Needs attention") : props.t("Setup complete"))}</StepLabel>
      <StepContent>
        {props.isFlagged ? <Stack spacing={1} alignItems="flex-start">
          <Typography variant="body1" color="error">
            {props.t("We're having issues with the connection to your store and the integration won't work until it is resolved. Please reconnect your store to restore the integration.")}
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              component={Link}
              to={props.connectUrl}
            >{props.t("Reconnect")}</Button>

            {props.flagErrorMessage ? <Button
              color="error"
              endIcon={<ExpandMoreRoundedIcon
                fontSize="small"
                sx={{
                  transform: errorDetailsOpened ? "scaleY(-1)" : "scaleY(1)",
                  transition: "transform 250ms"
                }}
              />}
              onClick={() => setErrorDetailsOpened(value => !value)}
            >{props.t("View details")}</Button> : null}
          </Stack>

          {props.flagErrorMessage ? <Collapse in={errorDetailsOpened}>
            <Alert severity="error">{props.flagErrorMessage}</Alert>
          </Collapse> : null}
        </Stack> : (props.isMissingScope ? <Stack spacing={1} alignItems="flex-start">
          <Typography variant="body1">
            {props.t("In order to enable some features of the integration, we need you to reconnect your store.")}
          </Typography>

          <Button
            variant="contained"
            component={Link}
            to={props.connectUrl}
          >{props.t("Reconnect")}</Button>
        </Stack> : props.connectedContent)}
      </StepContent>
    </Step>
  </Stepper>;
}
